import { ManagerOptions, Socket, SocketOptions, io } from "socket.io-client";
import requestConfig from "../../shared/config/request";
import { getSessionJWT } from "@/infra/auth/helper";

const MAX_QUEUE_SIZE = 100;

export const getSocketOptions = (
): Partial<SocketOptions | ManagerOptions> => ({
  transports: ["websocket"],
  upgrade: true,
  rememberUpgrade: false,
  forceNew: true,
  closeOnBeforeunload: false,
  reconnection: true,
  reconnectionAttempts: 3,
  withCredentials: true,
  path: requestConfig.socketPath
});

export let mainSocket: Socket | undefined = undefined;

const emitQueue: { event: string; args: any[] }[] = [];
let isReconnecting = false;

export const getZaniaSocket = (): Socket => {
  const token = getSessionJWT();

  if (!token) {
    throw new Error(
      "Failed to authenticate. Please try logging in again. Contact Zania support if the issue persists.",
    );
  }

  if (mainSocket) {
    return mainSocket;
  }

  mainSocket = io(`${requestConfig.socketUrl}/`, getSocketOptions());

  // When connected, flush queued emits
  mainSocket.on("connect", () => {
    isReconnecting = false;
    while (emitQueue.length > 0) {
      const { event, args } = emitQueue.shift()!;
      mainSocket?.emit(event, ...args);
    }
  });

  // If reconnect fails, clear the emit queue
  mainSocket.on("reconnect_failed", () => {
    console.error("[Socket] Reconnect failed. Clearing queued emits.");
    emitQueue.length = 0;
    isReconnecting = false;
  });

  return mainSocket;
};

// Safe emit function
export const safeEmit = (event: string, ...args: any[]) => {
  const socket = getZaniaSocket();

  if (socket.connected) {
    socket.emit(event, ...args);
  } else {
    if (emitQueue.length >= MAX_QUEUE_SIZE) {
      console.warn(
        `[Socket] Emit queue full (${MAX_QUEUE_SIZE}). Dropping oldest event to make room for "${event}".`
      );
      emitQueue.shift(); // Remove the oldest event
    }

    console.warn(`[Socket] Not connected. Queuing emit for "${event}".`);
    
    emitQueue.push({ event, args });

    if (!isReconnecting) {
      isReconnecting = true;
      socket.connect(); // will trigger queued emit on successful connect
    }
  }
};
